import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydModal from '../../../../commonDesign/SydModal'
import SaveCancelButtons from '../../../../molecules/SaveCancelButtons'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import { useModifyBranding } from '../../../../../api/abundanceEngine'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import { modalStyles } from './brandModalStyles'
import { useBrandImageManager } from './useBrandImageManager'
import { BrandImageModals } from './BrandImageModals'
import { ImageUrlField } from './ImageUrlField'

const BrandEditModal = ({ open, onClose, brandSchema }) => {
  const classes = modalStyles()
  const { mutateAsync: modifyBrand, isLoading } = useModifyBranding()
  const [error, setError] = useState(null)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      brandKey: brandSchema?.brandKey,
      firmName: brandSchema?.firmName,
      tagLine: brandSchema?.tagLine,
      logoUrl: brandSchema?.logoUrl,
      faviconUrl: brandSchema?.data?.icons?.favicon
    }
  })

  const { openAddModal, handleShowImageList, imageModalProps } = useBrandImageManager(form)

  const onSubmit = useCallback(async (data) => {
    setError(null)
    try {
      const result = await modifyBrand(data)
      if (result && result.success) {
        onClose()
      }
    } catch (err) {
      console.error('Error saving branding:', err)
      setError(err?.response?.data?.message || err?.message || 'An error occurred while saving')
    }
  }, [onClose, modifyBrand])

  return (
    <>
      <SydModal open={open} onClose={onClose} title='Modify Branding'>
        <div className={classes.inputRow}>
          <Controller
            name='brandKey'
            control={form.control}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Brand Key'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydInput {...field} error={fieldState.error} disabled />
              </SydLabel>
            )}
          />
          <Controller
            name='firmName'
            control={form.control}
            rules={{ required: { value: true, message: 'Firm name is required' } }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Firm Name'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydInput {...field} error={fieldState.error} />
              </SydLabel>
            )}
          />
        </div>
        <div className={classes.inputRow}>
          <Controller
            name='tagLine'
            control={form.control}
            rules={{ required: { value: true, message: 'Tag line is required' } }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Tag Line'
                error={hookFormErrorAdapter(form, fieldState)}
                className={classes.formLabel}
              >
                <SydInput {...field} error={fieldState.error} />
              </SydLabel>
            )}
          />
        </div>
        <ImageUrlField
          name='logoUrl'
          label='Logo URL'
          form={form}
          onAddImage={openAddModal}
          onSelectImage={handleShowImageList}
        />
        <ImageUrlField
          name='faviconUrl'
          label='Favicon URL'
          form={form}
          onAddImage={openAddModal}
          onSelectImage={handleShowImageList}
        />

        {error && (
          <div className={classes.errorMessage}>
            <ErrorDisplay error={error} />
          </div>
        )}

        <div className={classes.buttonRow}>
          <SaveCancelButtons
            containerClassName={classes.saveCancel}
            onSave={form.handleSubmit(onSubmit)}
            onCancel={onClose}
            isSubmitting={isLoading}
          />
        </div>
      </SydModal>

      <BrandImageModals {...imageModalProps} />
    </>
  )
}

BrandEditModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  brandSchema: PropTypes.object
}

export default BrandEditModal
